import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiCpu, FiSliders, FiTrendingUp } from "react-icons/fi";

const initialForm = {
  user_email: "",
  unique_users: "",
  total_documents: "",
  average_doc_size_mb: "",
  avg_words_per_doc: "",
  total_data_gb: "",
  annual_growth_percent: "",
  ai_requests_per_month: "",
  avg_words_per_prompt: "",
  avg_words_per_response: "",
};

const sections = [
  {
    title: "User activity",
    hint: "How many people will use Aivio.",
    fields: [
      { name: "unique_users", label: "Unique users per month", placeholder: "150" },
    ],
  },
  {
    title: "Documents and data",
    hint: "The content Aivio will learn from.",
    fields: [
      { name: "total_documents", label: "Documents to process", placeholder: "10,000" },
      { name: "average_doc_size_mb", label: "Average document size", placeholder: "2.5", unit: "MB", step: "0.1" },
      { name: "avg_words_per_doc", label: "Average words per document", placeholder: "1,200" },
      { name: "total_data_gb", label: "Total data volume", placeholder: "25.4", unit: "GB", step: "0.1" },
      { name: "annual_growth_percent", label: "Expected annual growth", placeholder: "15", unit: "%" },
    ],
  },
  {
    title: "AI interactions",
    hint: "How often people will ask questions.",
    fields: [
      { name: "ai_requests_per_month", label: "AI requests per month", placeholder: "5,000" },
      { name: "avg_words_per_prompt", label: "Average words per question", placeholder: "100" },
      { name: "avg_words_per_response", label: "Average words per answer", placeholder: "150" },
    ],
  },
];

const reasons = [
  { icon: <FiSliders />, text: "Size the solution to your actual needs" },
  { icon: <FiCpu />, text: "Estimate infrastructure and AI processing resources" },
  { icon: <FiTrendingUp />, text: "Recommend a pricing model that fits your volume and growth" },
];

function KnowYourCost() {
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    for (let key in formData) {
      data.append(key, formData[key]);
    }

    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}submit-usage`,
      headers: {
        "api-key": `${process.env.REACT_APP_API_KEY}`,
      },
      data: data,
    };

    try {
      const response = await axios(config);
      toast.success(response.data.message || "Submitted successfully!");
      setSubmitted(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialForm);
    setSubmitted(false);
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <main className="cost-page">
        <section className="cost-hero">
          <div className="container">
            <div className="eyebrow"><span className="eyebrow-dot" /> Pricing estimate</div>
            <h1>Know your cost <span>before you commit.</span></h1>
            <p>Share a few details about your expected usage and we’ll email you a transparent cost estimate for Aivio. It takes about two minutes.</p>
          </div>
        </section>

        <section className="container cost-layout">
          <aside className="cost-aside">
            <h2>Why we ask</h2>
            <ul className="cost-reasons">{reasons.map((reason) => <li key={reason.text}><span>{reason.icon}</span>{reason.text}</li>)}</ul>
            <div className="cost-note"><b>Approximate values are fine.</b> We’ll confirm the details with you before any proposal.</div>
            <div className="cost-next">
              <h3>What happens next</h3>
              <ol><li>We review your usage profile</li><li>You receive an estimate by email</li><li>We walk you through the options</li></ol>
            </div>
          </aside>

          <div className="cost-card">
            {submitted ? (
              <div className="cost-success" aria-live="polite">
                <span className="cost-success-mark">✓</span>
                <h2>Thanks, your details are in.</h2>
                <p>We’ll send your cost estimate to <b>{formData.user_email}</b>. In the meantime, explore how Aivio fits your workflows.</p>
                <div className="cost-success-actions"><Link to="/use-cases" className="btn hero-primary">Explore use cases <span aria-hidden="true">→</span></Link><button type="button" className="hero-secondary" onClick={resetForm}>Submit another estimate</button></div>
              </div>
            ) : (
              <form className="cost-form" onSubmit={handleSubmit}>
                <div className="cost-section">
                  <div className="cost-section-head"><span>01</span><div><h2>Where should we send it?</h2><p>Your estimate is emailed to this address.</p></div></div>
                  <label className="form-label" htmlFor="cost-user_email">Work email</label>
                  <input id="cost-user_email" className="form-control" type="email" name="user_email" placeholder="you@company.com" value={formData.user_email} onChange={handleChange} autoComplete="email" required />
                </div>

                {sections.map((section, index) => (
                  <div className="cost-section" key={section.title}>
                    <div className="cost-section-head"><span>{String(index + 2).padStart(2, "0")}</span><div><h2>{section.title}</h2><p>{section.hint}</p></div></div>
                    <div className="cost-fields">
                      {section.fields.map((field) => (
                        <div className="cost-field" key={field.name}>
                          <label className="form-label" htmlFor={`cost-${field.name}`}>{field.label}</label>
                          <div className={`cost-input${field.unit ? " has-unit" : ""}`}>
                            <input id={`cost-${field.name}`} className="form-control" type="number" min="0" inputMode="decimal" name={field.name} placeholder={`e.g. ${field.placeholder}`} step={field.step} value={formData[field.name]} onChange={handleChange} required />
                            {field.unit && <span>{field.unit}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="cost-submit">
                  <p>We only use these details to prepare your estimate. See our <Link to="/privacy-policy">privacy policy</Link>.</p>
                  <button type="submit" className="btn hero-primary" disabled={submitting}>
                    {submitting ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Sending…</> : <>Get my estimate <span aria-hidden="true">→</span></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>
    </>
  );
}

export default KnowYourCost;
